import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "@/context/AuthContext";
import { MileageProvider } from "@/context/MileageContext";
import AuthCallback from "@/components/AuthCallback";
import { RequireAuth, RequirePremium } from "@/components/RouteGuards";
import Landing from "@/pages/Landing";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Paywall from "@/pages/Paywall";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Welcome from "@/pages/Welcome";
import Dashboard from "@/pages/Dashboard";
import Receipts from "@/pages/Receipts";
import ReceiptDetail from "@/pages/ReceiptDetail";
import Upload from "@/pages/Upload";
import Inbox from "@/pages/Inbox";
import BankFeed from "@/pages/BankFeed";
import Billing from "@/pages/Billing";
import Enterprise from "@/pages/Enterprise";
import AdminOracleQA from "@/pages/AdminOracleQA";
import AdminSupport from "@/pages/AdminSupport";
import Profile from "@/pages/Profile";
import InvitePreview from "@/pages/InvitePreview";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Status from "@/pages/Status";
import { Toaster } from "sonner";
import AIAssistant from "@/components/AIAssistant";
import DevConsole from "@/components/DevConsole";
import CardAuthTicker from "@/components/CardAuthTicker";
import "@/App.css";

function InnerRoutes() {
  // Detect OAuth callback synchronously during render (prevents race conditions)
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/legacy" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/invite/:token" element={<InvitePreview />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/status" element={<Status />} />

      {/* Payment flow */}
      <Route
        path="/paywall"
        element={
          <RequireAuth>
            <Paywall />
          </RequireAuth>
        }
      />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<Navigate to="/paywall?cancelled=1" replace />} />
      <Route
        path="/welcome"
        element={
          <RequirePremium>
            <Welcome />
          </RequirePremium>
        }
      />

      {/* Premium-gated app */}
      <Route path="/dashboard" element={<RequirePremium><Dashboard /></RequirePremium>} />
      <Route path="/receipts" element={<RequirePremium><Receipts /></RequirePremium>} />
      <Route path="/receipts/:id" element={<RequirePremium><ReceiptDetail /></RequirePremium>} />
      <Route path="/upload" element={<RequirePremium><Upload /></RequirePremium>} />
      <Route path="/inbox" element={<RequirePremium><Inbox /></RequirePremium>} />
      <Route path="/bank-feed" element={<RequirePremium><BankFeed /></RequirePremium>} />
      <Route path="/billing" element={<RequirePremium><Billing /></RequirePremium>} />
      <Route path="/enterprise" element={<RequirePremium><Enterprise /></RequirePremium>} />
      <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
      <Route path="/admin/oracle-qa" element={<RequireAuth><AdminOracleQA /></RequireAuth>} />
      <Route path="/admin/support" element={<RequireAuth><AdminSupport /></RequireAuth>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Force the cosmic nebula onto html + body at runtime. Writing this in
  // /app/frontend/src/index.css triggers CRA's css-loader to attempt to
  // resolve the `/assets/...` url as a webpack module, which fails.
  // Setting the exact same CSS via JS on mount bypasses the loader and
  // guarantees the backdrop is applied at the root, covering 100vh on
  // every route (landing, login, paywall, dashboard) without clipping.
  useEffect(() => {
    const rules = {
      backgroundImage: "url('/assets/cosmic-nebula-core.png')",
      backgroundSize: "100% 100%",
      backgroundRepeat: "no-repeat",
      backgroundAttachment: "fixed",
      backgroundPosition: "center center",
      minHeight: "100vh",
      margin: "0",
      padding: "0",
    };
    Object.assign(document.documentElement.style, rules);
    Object.assign(document.body.style, rules);
  }, []);

  return (
    <AuthProvider>
      <MileageProvider>
      <BrowserRouter>
        <InnerRoutes />
        <AIAssistant />
        <DevConsole />
        <CardAuthTicker />
        <Toaster
          position="top-right"
          theme="dark"
          toastOptions={{
            style: {
              background: "rgba(15,23,42,0.95)",
              border: "1px solid rgba(16,185,129,0.25)",
              color: "#f8fafc",
            },
          }}
        />
      </BrowserRouter>
      </MileageProvider>
    </AuthProvider>
  );
}

export default App;
